import { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Car, Upload, X, ArrowLeft, Loader2, ImagePlus } from "lucide-react";
import { createMyVehicle, uploadVehiclePhotos, CreateVehiclePayload } from "@/Calls/callCustomer";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";

const MAKES = ["Acura","Alfa Romeo","Aston Martin","Audi","Bentley","BMW","Buick","Cadillac","Chevrolet","Chrysler","Dodge","Ferrari","FIAT","Ford","Genesis","GMC","Honda","Hyundai","INFINITI","Jaguar","Jeep","Kia","Lamborghini","Land Rover","Lexus","Lincoln","Maserati","Mazda","McLaren","Mercedes-Benz","MINI","Mitsubishi","Nissan","Porsche","Ram","Rolls-Royce","Subaru","Tesla","Toyota","Volkswagen","Volvo","Other"];
const CONDITIONS = ["EXCELLENT", "GOOD", "FAIR", "POOR"];
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 35 }, (_, i) => String(CURRENT_YEAR - i));

export default function CustomerVehicleUploadPage() {
    const navigate = useNavigate();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [form, setForm] = useState<{
        trade_type: "TRADE_IN" | "FSBO";
        trade_year: string;
        trade_make: string;
        trade_model: string;
        trade_trim: string;
        trade_color: string;
        trade_vin: string;
        trade_miles: string;
        trade_price: string;
        trade_payoff: string;
        trade_condition: string;
        trade_description: string;
    }>({
        trade_type: "TRADE_IN",
        trade_year: "",
        trade_make: "",
        trade_model: "",
        trade_trim: "",
        trade_color: "",
        trade_vin: "",
        trade_miles: "",
        trade_price: "",
        trade_payoff: "",
        trade_condition: "GOOD",
        trade_description: "",
    });

    const [photos, setPhotos] = useState<File[]>([]);
    const [previews, setPreviews] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const set = (field: keyof typeof form) => (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

    const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files ?? []);
        const valid = files.filter((f) => f.size <= 10 * 1024 * 1024);
        setPhotos((prev) => [...prev, ...valid]);
        valid.forEach((f) => {
            const reader = new FileReader();
            reader.onload = (ev) => setPreviews((p) => [...p, ev.target?.result as string]);
            reader.readAsDataURL(f);
        });
    };

    const removePhoto = (i: number) => {
        setPhotos((p) => p.filter((_, idx) => idx !== i));
        setPreviews((p) => p.filter((_, idx) => idx !== i));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!form.trade_year || !form.trade_make || !form.trade_model) {
            setError("Year, make, and model are required.");
            return;
        }

        setLoading(true);
        try {
            const payload: CreateVehiclePayload = {
                trade_type: form.trade_type,
                trade_year: Number(form.trade_year),
                trade_make: form.trade_make,
                trade_model: form.trade_model,
                trade_trim: form.trade_trim || undefined,
                trade_color: form.trade_color || undefined,
                trade_vin: form.trade_vin || undefined,
                trade_miles: form.trade_miles ? Number(form.trade_miles) : undefined,
                trade_price: form.trade_price ? Number(form.trade_price) : undefined,
                trade_payoff: form.trade_payoff ? Number(form.trade_payoff) : undefined,
                trade_condition: form.trade_condition || undefined,
                trade_description: form.trade_description || undefined,
            };

            const vehicle = await createMyVehicle(payload);

            if (photos.length > 0) {
                await uploadVehiclePhotos(vehicle.trade_id, photos);
            }

            navigate("/customer/vehicles", { replace: true });
        } catch (err) {
            if (err instanceof Error) setError(err.message);
            else setError("Failed to submit. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const isFSBO = form.trade_type === "FSBO";

    return (
        <div className="max-w-3xl mx-auto space-y-6">
            <div className="flex items-center gap-3">
                <Link to="/customer/vehicles">
                    <Button variant="ghost" size="sm">
                        <ArrowLeft className="h-4 w-4 mr-1" />
                        Back
                    </Button>
                </Link>
                <div>
                    <h1 className="text-2xl font-light uppercase tracking-wide">List a Vehicle</h1>
                    <p className="text-muted-foreground text-sm">Trade-in or sell privately</p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Listing Type */}
                <Card className="p-5">
                    <h2 className="text-sm font-medium uppercase tracking-wide mb-4">Listing Type</h2>
                    <div className="grid grid-cols-2 gap-3">
                        {(["TRADE_IN", "FSBO"] as const).map((t) => (
                            <button
                                key={t}
                                type="button"
                                onClick={() => setForm((p) => ({ ...p, trade_type: t }))}
                                className={`p-4 rounded-xl border-2 text-left transition-all ${
                                    form.trade_type === t
                                        ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30"
                                        : "border-border hover:border-indigo-300"
                                }`}
                            >
                                <p className="font-medium text-sm">
                                    {t === "TRADE_IN" ? "Trade-In" : "For Sale by Owner"}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {t === "TRADE_IN"
                                        ? "Submit to a dealer for trade value"
                                        : "List publicly for private buyers"}
                                </p>
                            </button>
                        ))}
                    </div>
                </Card>

                {/* Vehicle Info */}
                <Card className="p-5 space-y-4">
                    <h2 className="text-sm font-medium uppercase tracking-wide">Vehicle Info</h2>

                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <label className="mb-1 block text-xs font-medium uppercase">Year *</label>
                            <select
                                value={form.trade_year}
                                onChange={set("trade_year")}
                                required
                                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            >
                                <option value="">Year</option>
                                {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="mb-1 block text-xs font-medium uppercase">Make *</label>
                            <select
                                value={form.trade_make}
                                onChange={set("trade_make")}
                                required
                                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            >
                                <option value="">Make</option>
                                {MAKES.map((m) => <option key={m} value={m}>{m}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="mb-1 block text-xs font-medium uppercase">Model *</label>
                            <Input
                                value={form.trade_model}
                                onChange={set("trade_model")}
                                required
                                placeholder="Camry, F-150…"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="mb-1 block text-xs font-medium uppercase">Trim</label>
                            <Input value={form.trade_trim} onChange={set("trade_trim")} placeholder="XLE, Sport…" />
                        </div>
                        <div>
                            <label className="mb-1 block text-xs font-medium uppercase">Color</label>
                            <Input value={form.trade_color} onChange={set("trade_color")} placeholder="Silver, Black…" />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="mb-1 block text-xs font-medium uppercase">VIN</label>
                            <Input
                                value={form.trade_vin}
                                onChange={set("trade_vin")}
                                placeholder="17-character VIN"
                                maxLength={17}
                            />
                        </div>
                        <div>
                            <label className="mb-1 block text-xs font-medium uppercase">Mileage</label>
                            <Input
                                type="number"
                                value={form.trade_miles}
                                onChange={set("trade_miles")}
                                placeholder="e.g. 45000"
                                min={0}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="mb-1 block text-xs font-medium uppercase">Condition</label>
                            <select
                                value={form.trade_condition}
                                onChange={set("trade_condition")}
                                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            >
                                {CONDITIONS.map((c) => (
                                    <option key={c} value={c}>{c.charAt(0) + c.slice(1).toLowerCase()}</option>
                                ))}
                            </select>
                        </div>

                        {isFSBO && (
                            <div>
                                <label className="mb-1 block text-xs font-medium uppercase">Asking Price ($)</label>
                                <Input
                                    type="number"
                                    value={form.trade_price}
                                    onChange={set("trade_price")}
                                    placeholder="e.g. 15000"
                                    min={0}
                                />
                            </div>
                        )}

                        {!isFSBO && (
                            <div>
                                <label className="mb-1 block text-xs font-medium uppercase">Payoff Amount ($)</label>
                                <Input
                                    type="number"
                                    value={form.trade_payoff}
                                    onChange={set("trade_payoff")}
                                    placeholder="Remaining loan balance"
                                    min={0}
                                />
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="mb-1 block text-xs font-medium uppercase">Description</label>
                        <Textarea
                            value={form.trade_description}
                            onChange={set("trade_description")}
                            placeholder="Any additional details, recent work, accessories…"
                            rows={3}
                        />
                    </div>
                </Card>

                {/* Photos */}
                <Card className="p-5 space-y-4">
                    <h2 className="text-sm font-medium uppercase tracking-wide">Photos</h2>
                    <p className="text-xs text-muted-foreground">Up to 20 photos — jpg, png, webp. Max 10 MB each.</p>

                    {previews.length > 0 && (
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                            {previews.map((src, i) => (
                                <div key={i} className="relative aspect-square rounded-lg overflow-hidden border">
                                    <img src={src} alt="" className="object-cover w-full h-full" />
                                    {i === 0 && (
                                        <span className="absolute top-1 left-1 bg-indigo-600 text-white text-[10px] px-1.5 py-0.5 rounded">
                                            Cover
                                        </span>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => removePhoto(i)}
                                        className="absolute top-1 right-1 bg-black/60 rounded-full p-0.5 text-white hover:bg-black/80"
                                    >
                                        <X className="h-3 w-3" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/heic"
                        multiple
                        onChange={handleFiles}
                        className="hidden"
                    />
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full border-2 border-dashed border-border rounded-xl p-6 flex flex-col items-center gap-2 hover:border-indigo-400 transition-colors"
                    >
                        <ImagePlus className="h-8 w-8 text-muted-foreground" />
                        <span className="text-sm font-medium">Click to add photos</span>
                        <span className="text-xs text-muted-foreground">First photo becomes the cover image</span>
                    </button>
                </Card>

                {error && <p className="text-red-500 text-sm text-center">{error}</p>}

                <div className="flex gap-3 pb-8">
                    <Link to="/customer/vehicles" className="flex-none">
                        <Button type="button" variant="outline">Cancel</Button>
                    </Link>
                    <Button
                        type="submit"
                        disabled={loading}
                        className="flex-1 bg-gradient-to-r from-indigo-500 to-violet-500 text-white"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                Submitting…
                            </>
                        ) : (
                            <>
                                <Upload className="h-4 w-4 mr-2" />
                                {isFSBO ? "List for Sale" : "Submit for Trade-In"}
                            </>
                        )}
                    </Button>
                </div>
            </form>
        </div>
    );
}
